import React, { useMemo, useState } from 'react';
import { Filter, TrendingUp, DollarSign, Target } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import {
    PLACEMENT_CATEGORIES,
    CATEGORY_ORDER,
    CATEGORY_ICONS,
    getPlacementInfo,
    groupPlacementsByCategory
} from '../constants/placements';
import PlacementSection from './PlacementSection';

const PlacementGroupedView = ({
    creatives,
    platform,
    creativeAnalysisCache,
    campaignAnalysis,
    expandedCreativeId,
    analyzingCreativeId,
    onToggleCreativeExpand,
    onAnalyzeCreative
}) => {
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Group creatives by their placement data
    const groupedData = useMemo(() => {
        const groups = {};

        creatives.forEach(creative => {
            // If creative has placement data, use it
            if (creative.placements && creative.placements.length > 0) {
                creative.placements.forEach(placement => {
                    const category = placement.category || PLACEMENT_CATEGORIES.OTHER;
                    if (!groups[category]) {
                        groups[category] = {
                            name: category,
                            placements: {},
                            creativeIds: new Set()
                        };
                    }

                    // Add placement if not exists
                    if (!groups[category].placements[placement.placementId]) {
                        groups[category].placements[placement.placementId] = {
                            ...placement,
                            creativeIds: [],
                            metrics: { spend: 0, revenue: 0, impressions: 0, clicks: 0 }
                        };
                    }

                    // Add creative to placement
                    groups[category].placements[placement.placementId].creativeIds.push(creative.id);
                    groups[category].creativeIds.add(creative.id);

                    // Aggregate metrics
                    const m = placement.metrics || {};
                    groups[category].placements[placement.placementId].metrics.spend += m.spend || 0;
                    groups[category].placements[placement.placementId].metrics.revenue += m.revenue || 0;
                    groups[category].placements[placement.placementId].metrics.impressions += m.impressions || 0;
                    groups[category].placements[placement.placementId].metrics.clicks += m.clicks || 0;
                });
            } else {
                // If no placement data, group all under "All Placements"
                const category = PLACEMENT_CATEGORIES.OTHER;
                if (!groups[category]) {
                    groups[category] = {
                        name: category,
                        placements: {},
                        creativeIds: new Set()
                    };
                }

                // Ensure the 'all' placement exists
                if (!groups[category].placements['all']) {
                    groups[category].placements['all'] = {
                        placementId: 'all',
                        name: 'All Placements',
                        icon: '📍',
                        category: category,
                        creativeIds: [],
                        metrics: { spend: 0, revenue: 0, impressions: 0, clicks: 0 }
                    };
                }

                groups[category].placements['all'].creativeIds.push(creative.id);
                groups[category].creativeIds.add(creative.id);

                // Use creative-level metrics
                const m = creative.metrics || {};
                groups[category].placements['all'].metrics.spend += m.spend || 0;
                groups[category].placements['all'].metrics.revenue += m.revenue || 0;
                groups[category].placements['all'].metrics.impressions += m.impressions || 0;
                groups[category].placements['all'].metrics.clicks += m.clicks || 0;
            }
        });

        // Convert placements object to array and sort
        Object.keys(groups).forEach(category => {
            groups[category].placements = Object.values(groups[category].placements)
                .sort((a, b) => (b.metrics.spend || 0) - (a.metrics.spend || 0));
            groups[category].creativeIds = Array.from(groups[category].creativeIds);
        });

        return groups;
    }, [creatives]);

    // Get sorted categories based on predefined order
    const sortedCategories = useMemo(() => {
        const categories = Object.keys(groupedData);
        return CATEGORY_ORDER.filter(cat => categories.includes(cat))
            .concat(categories.filter(cat => !CATEGORY_ORDER.includes(cat)));
    }, [groupedData]);

    // Calculate total stats
    const totalStats = useMemo(() => {
        return Object.values(groupedData).reduce((acc, group) => {
            group.placements.forEach(p => {
                acc.spend += p.metrics.spend || 0;
                acc.revenue += p.metrics.revenue || 0;
            });
            return acc;
        }, { spend: 0, revenue: 0 });
    }, [groupedData]);

    const totalRoas = totalStats.spend > 0 ? totalStats.revenue / totalStats.spend : 0;

    // Filter categories if a specific one is selected
    const displayCategories = selectedCategory === 'all'
        ? sortedCategories
        : sortedCategories.filter(cat => cat === selectedCategory);

    return (
        <div>
            {/* Summary Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)'
            }}>
                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border-light)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <DollarSign size={16} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Total Spend</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>
                        {formatCurrency(totalStats.spend)}
                    </div>
                </div>

                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border-light)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <TrendingUp size={16} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Total Revenue</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>
                        {formatCurrency(totalStats.revenue)}
                    </div>
                </div>

                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border-light)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <Target size={16} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Average ROAS</span>
                    </div>
                    <div style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: 700,
                        color: totalRoas >= 2 ? 'var(--color-success)' : totalRoas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                    }}>
                        {totalRoas.toFixed(2)}x
                    </div>
                </div>
            </div>

            {/* Category Filter */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
                flexWrap: 'wrap'
            }}>
                <Filter size={16} className="text-secondary" />
                <button
                    onClick={() => setSelectedCategory('all')}
                    style={{
                        padding: 'var(--space-2) var(--space-3)',
                        borderRadius: 'var(--radius-full)',
                        border: 'none',
                        background: selectedCategory === 'all' ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                        color: selectedCategory === 'all' ? 'white' : 'var(--color-text-primary)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        transition: 'all var(--transition-fast)'
                    }}
                >
                    All Categories
                </button>
                {sortedCategories.map(category => (
                    <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        style={{
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-full)',
                            border: 'none',
                            background: selectedCategory === category ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                            color: selectedCategory === category ? 'white' : 'var(--color-text-primary)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            transition: 'all var(--transition-fast)'
                        }}
                    >
                        <span>{CATEGORY_ICONS[category] || '📍'}</span>
                        <span>{category}</span>
                        <span style={{
                            background: selectedCategory === category ? 'rgba(255,255,255,0.2)' : 'var(--color-bg-secondary)',
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-xs)'
                        }}>
                            {groupedData[category]?.creativeIds?.length || 0}
                        </span>
                    </button>
                ))}
            </div>

            {/* Placement Sections */}
            {displayCategories.length > 0 ? (
                displayCategories.map((category, index) => (
                    <PlacementSection
                        key={category}
                        category={category}
                        placements={groupedData[category].placements}
                        creatives={creatives}
                        creativeAnalysisCache={creativeAnalysisCache}
                        campaignAnalysis={campaignAnalysis}
                        expandedCreativeId={expandedCreativeId}
                        analyzingCreativeId={analyzingCreativeId}
                        onToggleCreativeExpand={onToggleCreativeExpand}
                        onAnalyzeCreative={onAnalyzeCreative}
                        defaultExpanded={index === 0}
                    />
                ))
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: 'var(--space-8)',
                    color: 'var(--color-text-secondary)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-lg)'
                }}>
                    No placement data available. Creatives will be grouped once placement data is fetched from the ad platform.
                </div>
            )}
        </div>
    );
};

export default PlacementGroupedView;
